// All onboarding copy for GetPaid's self-onboarding flow: the stepper, the
// settlement-partner terms step, the business/individual verification forms and
// their document uploads, the Pix bank-account step, the live status panel
// (approved / pending / rejected with warnings), post-approval editing, and the
// final "create your first invoice" CTA. Consumer-grade, plain-language tone —
// no crypto wording (fiat only; BRL settled via Pix). EN is the source of
// truth; onboarding.pt-BR.ts mirrors every key.
const onboarding: Record<string, string> = {
  // Page header
  'onboarding.title': 'Get set up to receive',
  'onboarding.description':
    'A few quick steps and you can start collecting from abroad — the money lands in your Pix in Brazilian reais.',
  'onboarding.loading': 'Loading…',
  'onboarding.error.load': 'Could not load your onboarding status. Refresh to try again.',

  // Stepper labels
  'onboarding.step.terms': 'Terms',
  'onboarding.step.identity': 'Identity',
  'onboarding.step.bankAccount': 'Pix',
  'onboarding.step.status': 'Review',

  // Step 1 — settlement-partner terms
  'onboarding.tos.title': 'Step 1 — Accept the terms',
  'onboarding.tos.subtitle':
    'Our regulated payments partner opens your settlement account. Review and accept their terms to continue.',
  'onboarding.tos.accept': 'Review & accept terms',
  'onboarding.tos.opening': 'Opening…',
  'onboarding.tos.recorded': 'Terms accepted.',

  // Step 2 — identity: business vs individual toggle
  'onboarding.entityType': 'How are you receiving?',
  'onboarding.entityType.hint':
    'Choose whether you receive as a business (company) or as an individual (yourself).',
  'onboarding.entityType.business': 'A business',
  'onboarding.entityType.individual': 'An individual',

  // Step 2 — business (KYB)
  'onboarding.kyb.title': 'Step 2 — Verify your business',
  'onboarding.kyb.subtitle':
    'We need to confirm your business so it can receive settlements in reais via Pix. All fields and documents are required.',
  'onboarding.kyb.submit': 'Submit for review',
  'onboarding.kyb.submitting': 'Submitting…',
  'onboarding.kyb.submitted': 'Submitted for review. We will email you as soon as it is approved.',

  // Step 2 — individual (KYC)
  'onboarding.kyc.title': 'Step 2 — Verify your identity',
  'onboarding.kyc.subtitle':
    'We need to confirm who you are so you can receive settlements in reais via Pix. All fields and documents are required.',
  'onboarding.kyc.resubmit.subtitle':
    'Re-enter your details, re-upload your documents, then submit again.',

  // Business form sections + fields
  'onboarding.kyb.section.business': 'Business details',
  'onboarding.kyb.section.address': 'Business address',
  'onboarding.kyb.section.docs': 'Business documents',
  'onboarding.kyb.section.owners': 'Owners',
  'onboarding.kyb.legalName': 'Legal business name',
  'onboarding.kyb.cnpj': 'CNPJ',
  'onboarding.kyb.taxId': 'Tax ID',
  'onboarding.kyb.cnpjHint': '14 digits — punctuation is removed automatically.',
  'onboarding.kyb.cnpjInvalid': 'The CNPJ must have 14 digits.',
  'onboarding.kyb.taxIdLocked':
    'The CNPJ cannot change after verification. Contact support if your legal entity changed.',
  'onboarding.kyb.formationDate': 'Formation date',
  'onboarding.kyb.email': 'Business email',
  'onboarding.kyb.website': 'Website',
  'onboarding.kyb.addressLine1': 'Address line 1',
  'onboarding.kyb.addressLine2': 'Address line 2 (optional)',
  'onboarding.kyb.city': 'City',
  'onboarding.kyb.state': 'State / region',
  'onboarding.kyb.postal': 'Postal code',
  'onboarding.kyb.country': 'Country',
  'onboarding.kyb.doc.incorporation': 'Incorporation document',
  'onboarding.kyb.doc.ownership': 'Proof of ownership',
  'onboarding.kyb.poaType': 'Proof of address — type',
  'onboarding.kyb.doc.poa': 'Proof of address — file',

  // Owner fields
  'onboarding.kyb.addOwner': 'Add owner',
  'onboarding.kyb.removeOwner': 'Remove',
  'onboarding.kyb.owner': 'Owner {n}',
  'onboarding.kyb.owner.role': 'Role',
  'onboarding.kyb.owner.firstName': 'First name',
  'onboarding.kyb.owner.lastName': 'Last name',
  'onboarding.kyb.owner.dob': 'Date of birth',
  'onboarding.kyb.owner.taxId': 'Tax ID (CPF)',
  'onboarding.kyb.owner.addr1': 'Address line 1',
  'onboarding.kyb.owner.city': 'City',
  'onboarding.kyb.owner.state': 'State / region',
  'onboarding.kyb.owner.postal': 'Postal code',
  'onboarding.kyb.owner.country': 'Country',
  'onboarding.kyb.owner.idDocType': 'ID document type',
  'onboarding.kyb.owner.idDocCountry': 'ID document country',
  'onboarding.kyb.owner.selfie': 'Selfie',
  'onboarding.kyb.owner.idFront': 'ID document — front',
  'onboarding.kyb.owner.poaType': 'Proof of address — type',
  'onboarding.kyb.owner.poa': 'Proof of address — file',

  // Owner / doc roles + option labels
  'onboarding.role.beneficialOwner': 'Beneficial owner',
  'onboarding.role.beneficialControlling': 'Beneficial owner + controlling',
  'onboarding.role.controllingPerson': 'Controlling person',
  'onboarding.idDoc.passport': 'Passport',
  'onboarding.idDoc.idCard': 'ID card',
  'onboarding.idDoc.drivers': "Driver's license",
  'onboarding.poa.utilityBill': 'Utility bill',
  'onboarding.poa.bankStatement': 'Bank statement',
  'onboarding.poa.rentalAgreement': 'Rental agreement',
  'onboarding.poa.taxDocument': 'Tax document',
  'onboarding.poa.governmentCorrespondence': 'Government correspondence',

  // Individual (KYC) form fields
  'onboarding.kyc.section.personal': 'Personal details',
  'onboarding.kyc.section.address': 'Address',
  'onboarding.kyc.section.docs': 'Identity documents',
  'onboarding.kyc.firstName': 'First name',
  'onboarding.kyc.lastName': 'Last name',
  'onboarding.kyc.dob': 'Date of birth',
  'onboarding.kyc.taxId': 'Tax ID (CPF)',
  'onboarding.kyc.taxId.generic': 'Tax ID',
  'onboarding.kyc.email': 'Email',
  'onboarding.kyc.addressLine1': 'Address line 1',
  'onboarding.kyc.addressLine2': 'Address line 2 (optional)',
  'onboarding.kyc.city': 'City',
  'onboarding.kyc.state': 'State / region',
  'onboarding.kyc.postal': 'Postal code',
  'onboarding.kyc.country': 'Country',
  'onboarding.kyc.idDocType': 'ID document type',
  'onboarding.kyc.idDocCountry': 'ID document country',
  'onboarding.kyc.selfie': 'Selfie',
  'onboarding.kyc.idFront': 'ID document — front',
  'onboarding.kyc.poaType': 'Proof of address — type',
  'onboarding.kyc.poa': 'Proof of address — file',
  'onboarding.kyc.missingDocs':
    'Upload your selfie, ID document, and proof of address before submitting.',

  // Upload states + validation
  'onboarding.upload.uploading': 'Uploading…',
  'onboarding.upload.uploaded': 'Uploaded ✓',
  'onboarding.kyb.docsOptionalHint': 'Optional — upload only to replace the document on file.',
  'onboarding.kyb.missingDocs':
    'Upload all required documents (business and each owner) before submitting.',
  'onboarding.kyb.ownersToggle': 'Also update owners',
  'onboarding.kyb.ownersToggleHint':
    'Owner details and documents must be re-submitted in full when updating owners.',

  // Step 3 — Pix bank account
  'onboarding.bank.title': 'Step 3 — Where should we send your money?',
  'onboarding.bank.subtitle':
    'Enter the Pix key where you want to receive. This is where every payout lands in reais.',
  'onboarding.bank.pixKey': 'Pix key',
  'onboarding.bank.pixKeyHint': 'CPF/CNPJ, phone, email, or a random Pix key.',
  'onboarding.bank.save': 'Save Pix key',
  'onboarding.bank.saving': 'Saving…',
  'onboarding.bank.saved': 'Pix key saved.',
  'onboarding.bank.savedTitle': 'Pix key on file',
  'onboarding.bank.savedBody': 'Your payouts will be sent to the Pix key you registered.',
  'onboarding.bank.update': 'Update Pix key',
  'onboarding.bank.locked': 'Verify your identity above before adding your Pix key.',

  // Step 4 — status panel (pending)
  'onboarding.status.pendingTitle': 'Review in progress',
  'onboarding.status.pendingBody':
    'Our partner is reviewing your details. This usually takes a little while — we will email you the moment it is done. You can leave this page.',

  // Status panel (rejected)
  'onboarding.status.rejectedTitle': 'We could not verify you',
  'onboarding.status.rejectedBody':
    'Our partner could not verify this submission. Check the points below, fix your details, and submit again.',
  'onboarding.kyb.resubmit.cta': 'Submit again',
  'onboarding.kyb.resubmit.submitting': 'Submitting…',
  'onboarding.kyb.resubmit.title': 'Fix and submit again',
  'onboarding.kyb.resubmit.subtitle':
    'We kept your previous details. Fix the points above, re-upload your documents, then submit again.',
  'onboarding.kyb.resubmit.done': 'Submitted — our partner is reviewing again.',
  'onboarding.warnings.none':
    'No specific reason was given. Double-check your details and documents, then submit again.',
  'onboarding.warnings.compliance': 'Compliance issues',
  'onboarding.warnings.fraud': 'Fraud checks',
  'onboarding.warnings.issue': 'Issue',

  // Status panel (approved) + edit
  'onboarding.approved.title': 'You are verified',
  'onboarding.approved.body':
    'Your identity is verified. Add or confirm your Pix key below, then you are ready to get paid.',
  'onboarding.edit.cta': 'Edit information',
  'onboarding.edit.cancel': 'Cancel editing',
  'onboarding.edit.title': 'Edit your information',
  'onboarding.edit.subtitle':
    "Update your details with our partner. Documents are optional — upload only what you want to replace. Changes are re-reviewed and may show as 'in review' until re-approved.",
  'onboarding.edit.note': 'You can keep receiving while the review is in progress.',
  'onboarding.edit.submit': 'Submit changes',
  'onboarding.edit.submitting': 'Submitting…',
  'onboarding.edit.done': 'Changes submitted — our partner is reviewing them.',

  // Read-only verification summary (KybReceiverPanel)
  'onboarding.detail.title': 'Verification summary',
  'onboarding.detail.legalName': 'Legal business name',
  'onboarding.detail.taxId': 'CNPJ',
  'onboarding.detail.formationDate': 'Formation date',
  'onboarding.detail.email': 'Email',
  'onboarding.detail.website': 'Website',
  'onboarding.detail.country': 'Country',
  'onboarding.detail.address': 'Address',
  'onboarding.detail.limits.title': 'Limits',
  'onboarding.detail.limits.perTransaction': 'Per transaction',
  'onboarding.detail.limits.daily': 'Daily',
  'onboarding.detail.limits.monthly': 'Monthly',
  'onboarding.detail.owners.title': 'Owners',
  'onboarding.detail.owners.role': 'Role',
  'onboarding.detail.owners.name': 'Name',
  'onboarding.detail.owners.dob': 'Date of birth',
  'onboarding.detail.owners.country': 'Country',
  'onboarding.detail.owners.taxId': 'Tax ID (CPF)',
  'onboarding.detail.status': 'Status',

  // Full completion — create first invoice CTA
  'onboarding.done.title': 'You are all set!',
  'onboarding.done.body':
    'Your identity is verified and your Pix key is on file. Create your first invoice and start getting paid.',
  'onboarding.done.cta': 'Create your first invoice',

  // Field placeholders / helpers
  'onboarding.select.placeholder': 'Select…',
};

export default onboarding;
